import type { Editor } from '@tiptap/core';

// ═══════════════════════════════════════════════════════════════
// Kopírovat formát (Format Painter), sekce 2.1
//
// Chování jako ve Wordu:
//   - klik na tlačítko = jedno použití (po aplikaci se nástroj vypne)
//   - dvojklik = trvalý režim (aplikuje se opakovaně, dokud se nevypne)
//
// Zkopíruje run-level formátování (font, velikost, řez, barva, zvýraznění)
// z aktuálního výběru a aplikuje ho na další vybraný text.
// ═══════════════════════════════════════════════════════════════

interface CapturedFormat {
  bold: boolean;
  italic: boolean;
  underline: boolean;
  strike: boolean;
  color?: string;
  highlight?: string;
  fontFamily?: string;
}

export class FormatPainter {
  private format: CapturedFormat | null = null;
  private persistent = false;
  private _active = false;

  constructor(
    private editor: Editor,
    private onChange: (active: boolean) => void,
  ) {}

  get active(): boolean {
    return this._active;
  }

  /** Klik: jednorázové použití. */
  activateOnce(): void {
    this.capture();
    this.persistent = false;
    this.setActive(!!this.format);
  }

  /** Dvojklik: trvalý režim. */
  activatePersistent(): void {
    this.capture();
    this.persistent = true;
    this.setActive(!!this.format);
  }

  cancel(): void {
    this.format = null;
    this.persistent = false;
    this.setActive(false);
  }

  /** Zavolat po změně výběru v editoru (mouseup / selection change). */
  onSelectionApply(): void {
    if (!this._active || !this.format) return;
    const { from, to } = this.editor.state.selection;
    if (from === to) return; // nic nevybráno

    this.apply(this.format);

    if (!this.persistent) this.cancel();
  }

  private capture(): void {
    const attrs = this.editor.getAttributes('textStyle');
    this.format = {
      bold: this.editor.isActive('bold'),
      italic: this.editor.isActive('italic'),
      underline: this.editor.isActive('underline'),
      strike: this.editor.isActive('strike'),
      color: attrs.color,
      highlight: this.editor.getAttributes('highlight').color,
      fontFamily: attrs.fontFamily,
    };
  }

  private apply(f: CapturedFormat): void {
    const chain = this.editor.chain().focus();

    // Nejdřív vyčisti relevantní marky, pak nastav dle zdroje.
    chain.unsetBold().unsetItalic().unsetUnderline().unsetStrike().unsetHighlight();
    if (f.bold) chain.setBold();
    if (f.italic) chain.setItalic();
    if (f.underline) chain.setUnderline();
    if (f.strike) chain.setStrike();
    if (f.color) chain.setColor(f.color);
    else chain.unsetColor();
    if (f.highlight) chain.setHighlight({ color: f.highlight });
    if (f.fontFamily) chain.setFontFamily(f.fontFamily);
    else chain.unsetFontFamily();

    chain.run();
  }

  private setActive(v: boolean): void {
    this._active = v;
    this.onChange(v);
  }
}
